/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";

export type MembershipPanelProps = {
  loading: boolean;
  error: string | null;
  email: string;
  onEmail: (value: string) => void;
  onEmailLookup: () => void;
  onReset: () => void;
};

/**
 * The membership result column. Two ways in: a scanned QR, or a typed email
 * (for a customer who left their phone at home) — both land on the same resolved
 * view. See docs/plans/loyalty/redemption_wiring.md §5.
 */
export const MembershipPanel = defineComponent<MembershipPanelProps>({
  name: "MembershipPanel",
  props: ["loading", "error", "email", "onEmail", "onEmailLookup", "onReset"],
  setup(props) {
    const { t } = useLocalization();
    const emailValid = computed(() =>
      /^\S+@\S+\.\S+$/.test(props.email.trim()),
    );

    return () => {
      if (props.error) {
        return (
          <div class="flex flex-col space-y-4 flex-1">
            <div class="alert alert-error shadow-lg">
              <span class="material-symbols-outlined">error</span>
              <span>{props.error}</span>
            </div>
            <button onClick={props.onReset} class="btn btn-ghost btn-sm w-full">
              <span class="material-symbols-outlined mr-2">refresh</span>
              {t("loyalty.modal.scan_again")}
            </button>
          </div>
        );
      }

      // Idle: waiting to scan, or look up by email.
      return (
        <div class="flex flex-col flex-1 space-y-5">
          <div class="space-y-2">
            <label
              for="loyalty-email"
              class="text-xs font-semibold uppercase text-base-content/60"
            >
              {t("loyalty.modal.lookup_by_email")}
            </label>
            <div class="flex flex-col gap-2">
              <input
                id="loyalty-email"
                type="email"
                disabled={props.loading}
                class="input input-bordered min-w-0 w-full"
                placeholder={t("loyalty.modal.email_placeholder")}
                value={props.email}
                onInput={(e) =>
                  props.onEmail((e.target as HTMLInputElement).value)
                }
                onKeydown={(e: KeyboardEvent) =>
                  e.key === "Enter" &&
                  !props.loading &&
                  emailValid.value &&
                  props.onEmailLookup()
                }
              />
              <button
                type="button"
                class="btn btn-primary"
                disabled={props.loading || !emailValid.value}
                onClick={props.onEmailLookup}
              >
                {t("loyalty.modal.look_up")}
              </button>
            </div>
          </div>
        </div>
      );
    };
  },
});
